// @ts-nocheck
if (!customElements.get('swym-wishlist-button')) {
  // Shared across every swym-wishlist-button instance on the page so collection/home
  // grids with many product cards don't fire one fetchLists()/SDK-ready wait and one
  // pair of global evtLayer listeners per card (that pattern also leaked listeners on
  // every filter/sort/paginate morph, since old instances were never unregistered).
  const instancesByVariantId = new Map();
  const wishlistState = {
    variantIds: new Set(),
    productIds: new Set(),
    loadPromise: null,
  };
  let globalListenersAttached = false;
  let swatPromise = null;
  let popupPromise = null;

  function loadAsset(tagName, attributes) {
    return new Promise((resolve, reject) => {
      const element = Object.assign(document.createElement(tagName), attributes);
      element.onload = resolve;
      element.onerror = reject;
      document.head.append(element);
    });
  }

  // The multi-list popup's markup, CSS and JS wait in a template (snippets/swym-multiple-wishlist-popup.liquid)
  // until the first tap that needs them, instead of costing every page load.
  function loadPopup() {
    if (!popupPromise) {
      const template = document.getElementById('swym-wishlist-popup-template');
      if (!(template instanceof HTMLTemplateElement)) return Promise.resolve(null);

      const { stylesheet, script } = template.dataset;
      popupPromise = (async () => {
        const hasStylesheet = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(
          (link) => link.getAttribute('href') === stylesheet
        );
        // Stylesheet first: it carries the rules that keep the popup's inactive states hidden.
        if (!hasStylesheet) await loadAsset('link', { rel: 'stylesheet', href: stylesheet });
        if (!document.querySelector('swym-wishlist-popup')) document.body.append(template.content.cloneNode(true));
        if (!customElements.get('swym-wishlist-popup')) await loadAsset('script', { src: script });
        await customElements.whenDefined('swym-wishlist-popup');
        return document.querySelector('swym-wishlist-popup');
      })().catch((err) => {
        popupPromise = null;
        console.error('Swym popup failed to load:', err);
        return null;
      });
    }
    return popupPromise;
  }

  // Swym's docs: never read window._swat directly, always go through SwymCallbacks
  // so calls can't race the SDK's own async init.
  function getSwat() {
    if (!swatPromise) {
      swatPromise = new Promise((resolve) => {
        if (window._swat) {
          resolve(window._swat);
          return;
        }
        window.SwymCallbacks = window.SwymCallbacks || [];
        window.SwymCallbacks.push(() => resolve(window._swat));
      });
    }
    return swatPromise;
  }

  function registerInstance(variantId, instance) {
    let set = instancesByVariantId.get(variantId);
    if (!set) {
      set = new Set();
      instancesByVariantId.set(variantId, set);
    }
    set.add(instance);
  }

  function unregisterInstance(variantId, instance) {
    const set = instancesByVariantId.get(variantId);
    if (!set) return;
    set.delete(instance);
    if (!set.size) instancesByVariantId.delete(variantId);
  }

  function notifyInstances(variantId, isAdded) {
    const set = instancesByVariantId.get(variantId);
    if (!set) return;
    set.forEach((instance) => instance.updateVisualState(isAdded));
  }

  function refreshAllInstancesVisualState() {
    instancesByVariantId.forEach((set, variantId) => {
      set.forEach((instance) => instance.updateVisualState(isInWishlist(variantId, instance.product?.empi)));
    });
  }

  // fetchLists() (not the undocumented fetch()) is what Swym's SDK-only-mode docs
  // point to for status checks, since it's list-aware: an item can be wishlisted in a
  // named list rather than the default one when "Collections" (multi-list) is enabled.
  function refreshWishlistState(swat) {
    return new Promise((resolve) => {
      swat.fetchLists({
        callbackFn: (lists) => {
          wishlistState.variantIds.clear();
          wishlistState.productIds.clear();
          (Array.isArray(lists) ? lists : []).forEach((list) => {
            (list.listcontents || []).forEach((item) => {
              if (item.epi != null) wishlistState.variantIds.add(Number(item.epi));
              if (item.empi != null) wishlistState.productIds.add(Number(item.empi));
            });
          });
          resolve();
        },
        errorFn: (err) => {
          console.error('Swym fetchLists Error:', err);
          resolve();
        },
      });
    });
  }

  function loadWishlistState(swat) {
    if (!wishlistState.loadPromise) {
      wishlistState.loadPromise = refreshWishlistState(swat);
    }
    return wishlistState.loadPromise;
  }

  function isInWishlist(variantId, productId) {
    return wishlistState.variantIds.has(variantId) || wishlistState.productIds.has(productId);
  }

  function attachGlobalListenersOnce(swat) {
    if (globalListenersAttached) return;
    globalListenersAttached = true;

    swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, (e) => {
      const variantId = e?.detail?.epi != null ? Number(e.detail.epi) : null;
      if (variantId == null) return;
      wishlistState.variantIds.add(variantId);
      notifyInstances(variantId, true);
    });

    swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, (e) => {
      const variantId = e?.detail?.epi != null ? Number(e.detail.epi) : null;
      if (variantId == null) return;
      wishlistState.variantIds.delete(variantId);
      notifyInstances(variantId, false);
    });

    // Fires once the SDK merges a guest session into a newly-logged-in customer's
    // account, so previously-wishlisted items surface without a full page reload.
    swat.evtLayer.addEventListener('sw:customerInfoRefreshed', () => {
      wishlistState.loadPromise = null;
      loadWishlistState(swat).then(refreshAllInstancesVisualState);
    });
  }

  class SwymWishlistButton extends HTMLElement {
    connectedCallback() {
      this.elements = {
        wishlistButton: this.querySelector('button'),
        unfilledState: this.querySelector('.swymUnfilled'),
        filledState: this.querySelector('.swymFilled'),
      };

      if (!this.elements.wishlistButton) return;

      this.initializeSwymButton();
    }

    disconnectedCallback() {
      if (this.variantId != null) unregisterInstance(this.variantId, this);
    }

    async initializeSwymButton() {
      const { epi, empi, du, title, image } = this.elements.wishlistButton.dataset;

      // Ensure IDs are valid numbers/strings and fall back gracefully
      const variantId = Number(epi);
      const productId = Number(empi);

      if (!variantId || !productId) {
        console.error("Swym Button Error: Missing data-epi or data-empi dataset attributes.", this.elements.wishlistButton);
        return;
      }

      const safeTitle = title ? title.replace(/[\n\r"\\]/g, '').trim() : "";
      const safeUrl = du ? du.trim() : "";

      const product = {
        epi: variantId,
        empi: productId,
        du: safeUrl,
        dt: safeTitle,
        iu: image ? image.trim() : ""
      };

      this.variantId = variantId;
      this.product = product;

      const swat = await getSwat();

      attachGlobalListenersOnce(swat);
      registerInstance(variantId, this);

      await loadWishlistState(swat);
      this.updateVisualState(isInWishlist(variantId, productId));

      // Click Event Listener
      this.elements.wishlistButton.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isCollectionsEnabled = typeof swat.isCollectionsEnabled === 'function'
          ? swat.isCollectionsEnabled()
          : false;

        if (isCollectionsEnabled) {
          const popupElement = await loadPopup();
          if (popupElement && typeof popupElement._show === 'function') {
            window.currentActiveProduct = {
              id: product.empi,
              title: safeTitle,
              url: product.du,
              featured_image: product.iu,
              variants: [{ id: product.epi }]
            };
            window.currentActiveVariantId = product.epi;
            window.currentActiveWishlistButton = this;
            popupElement._show();
          } else {
            console.error("Swym Multi-list popup element not found or invalid on page.");
          }
        } else {
          const isCurrentlyAdded = this.elements.wishlistButton.classList.contains('swym-added-custom');

          if (isCurrentlyAdded) {
            swat.removeFromWishList(
              product,
              () => this.updateVisualState(false),
              (err) => console.error('Swym Remove Error:', err)
            );
          } else {
            swat.addToWishList(
              product,
              () => this.updateVisualState(true),
              (err) => console.error('Swym Add Error:', err)
            );
          }
        }
      });
    }

    updateVisualState(isAdded) {
      const btn = this.elements.wishlistButton;
      if (!btn) return;

      const productTitle = this.product?.dt || btn.dataset.title || '';

      if (isAdded) {
        btn.classList.add('swym-added-custom');
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', `Remove ${productTitle} from wishlist`);
        if (this.elements.unfilledState) this.elements.unfilledState.style.display = 'none';
        if (this.elements.filledState) this.elements.filledState.style.display = 'flex';
      } else {
        btn.classList.remove('swym-added-custom');
        btn.setAttribute('aria-label', `Remove ${productTitle} from wishlist`);
        if (this.elements.unfilledState) this.elements.unfilledState.style.display = 'flex';
        if (this.elements.filledState) this.elements.filledState.style.display = 'none';
      }
    }
  }

  customElements.define('swym-wishlist-button', SwymWishlistButton);
}
